import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import useSWR, { mutate } from "swr";
import {
  AppConstants,
  AppRouteUi,
  PlatformApi,
  topLoaderProgress,
} from "../../configs";
import { AxiosAuth } from "../../core";
import {
  ApiResponse,
  ApiSchemaCreateVendorLanguage,
  ApiSchemaVendorLanguageData,
  WithPagination,
} from "../../models";
import { UtilsJQuery } from "../../utils";
import { ApiSchemaLanguageData } from "../../models/api/language";

type pageVendorProps = {
  pageSize: number;
  pageNo: number;
  searchText?: string;
  setLoaderProgress: any;
};
export function useFunctionalityPagedVendorLanguage(props: pageVendorProps) {
  const { pageSize, pageNo, searchText, setLoaderProgress } = props;

  const [pagedVendorLanguageListCount, setPagedVendorLanguageListCount] =
    useState(0);
  const vendorId = AppRouteUi.Vendors.Profile.useParam();
  const pagedVendorLanguageUrl = PlatformApi.Vendors.Language.GetLanguageById(
    vendorId,
    pageSize,
    pageNo,
    searchText
  );

  const { data: apiPagedVendorLanguageList } = useSWR<
    ApiSchemaVendorLanguageData[]
  >(pagedVendorLanguageUrl, () => {
    setLoaderProgress(topLoaderProgress.start);
    return AxiosAuth.get<
      ApiResponse<WithPagination<ApiSchemaVendorLanguageData[]>>
    >(pagedVendorLanguageUrl)
      .then((r) => {
        setLoaderProgress(topLoaderProgress.complete);
        setPagedVendorLanguageListCount(r.data.data.count);
        return r.data.data.data;
      })
      .catch((e) => {
        setLoaderProgress(topLoaderProgress.complete);
        toast.error("Failed to load vendor data.");
        return e;
      });
  });
  const pagedVendorLanguageList = useMemo(() => {
    const data = Array.isArray(apiPagedVendorLanguageList)
      ? apiPagedVendorLanguageList
      : [];
    return data;
  }, [apiPagedVendorLanguageList]);

  return {
    pagedVendorLanguageList,
    pagedVendorLanguageListCount,
    pagedVendorLanguageUrl,
  };
}

type Props = {
  setAddModalShow?: (value: boolean) => void;
  setDeleteModalShow?: (value: boolean) => void;
  shouldVendorLanguageGetAllInvoke?: boolean;
};
export function useFunctionalityVendorLanguage(props: Props) {
  const {
    setAddModalShow,
    setDeleteModalShow,
    shouldVendorLanguageGetAllInvoke = false,
  } = props;
  const vendorId = AppRouteUi.Vendors.Profile.useParam();
  const getVendorLanguageUrl =
    PlatformApi.Vendors.Language.GetAllLanguageById(vendorId);

  const { data: apiVendorLanguageData, error, isLoading, isValidating } = useSWR<ApiSchemaCreateVendorLanguage[]>(
    shouldVendorLanguageGetAllInvoke ? getVendorLanguageUrl : null,
    async () => {
      const res = await AxiosAuth.get<ApiResponse<WithPagination<ApiSchemaCreateVendorLanguage[]>>>(
        getVendorLanguageUrl
      )
      const outer = res?.data?.data ?? null;

      if (outer && typeof outer === "object" && "data" in (outer as any)) {
        const inner = (outer as any).data;
        return Array.isArray(inner) ? inner : [];
      }

      return Array.isArray(outer) ? outer : [];
    },
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  const vendorLanguageList = React.useMemo(() => {
    return Array.isArray(apiVendorLanguageData) ? apiVendorLanguageData : [];
  }, [apiVendorLanguageData]);

  const onAddLanguage = React.useCallback(
    async (form: ApiSchemaCreateVendorLanguage) => {
      const btnLoading = UtilsJQuery.Ladda(".login-from-submit-btn");
      btnLoading.start?.();
      const formToSubmit = {
        name: form.name,
        code: form.code,
        vendorId: vendorId,
        languageId: form.languageId
      }
      try {
        const res = await AxiosAuth.post(
          PlatformApi.Vendors.Language.AddLanguage(),
          formToSubmit
        );

        if ((res.status === AppConstants.Api.StatusOK)) {
          mutate(getVendorLanguageUrl);
          toast.success("Language added successful");
          setAddModalShow?.(false);
        }
      } catch (e) {
        toast.error("Adding language failed");
      }
      btnLoading.stop?.();
      return Promise.resolve(null);
    },
    []
  );

  const onUpdateLanguage = React.useCallback(
    async (id: string, form: ApiSchemaCreateVendorLanguage) => {
      const btnLoading = UtilsJQuery.Ladda(".login-from-submit-btn");
      btnLoading.start?.();

      const formToSubmit: ApiSchemaCreateVendorLanguage = {
        name: form.name.trim(),
        code: form.code,
        vendorId: vendorId,
        languageId: form.languageId,
      };

      try {
        const res = await AxiosAuth.put(
          PlatformApi.Vendors.Language.UpdateLanguage(id),
          formToSubmit
        );

        if (res.status === AppConstants.Api.StatusOK) {
          mutate(getVendorLanguageUrl);
          toast.success("Language updated successfully");
          setAddModalShow?.(false);
        }
      } catch (e) {
        toast.error("Updating language failed");
        throw e;
      } finally {
        btnLoading.stop?.();
      }

      return null;
    },
    [getVendorLanguageUrl, setAddModalShow, vendorId]
  );


  const onDelete = React.useCallback(async (langId: string) => {
    const btnLoading = UtilsJQuery.Ladda(".user-from-submit-btn");
    btnLoading.start?.();
    try {
      const res = await AxiosAuth.delete<ApiSchemaLanguageData>(
        PlatformApi.Vendors.Language.DeleteLanguage(langId)
      );
      if (res.status === AppConstants.Api.StatusOK) {
        mutate(getVendorLanguageUrl);
        toast.success("Deleted successfully");
        setDeleteModalShow?.(false);
      }
    } catch (e) {
      toast.error("Delete failed");
    }

    btnLoading.stop?.();
    return Promise.resolve(null);
  }, [getVendorLanguageUrl, setDeleteModalShow]);

  return {
    vendorLanguageList,
    onAddLanguage,
    onUpdateLanguage,
    onDelete,
  };
}

type UsePlatformLanguagesProps = {
  shouldLanguageGetAllInvoke?: boolean;
};

export function useFunctionalityPlatformLanguages(
  // props: UsePlatformLanguagesProps = {}
) {
  const shouldLanguageGetAllInvoke = false;

  const getAllLanguagesUrl = PlatformApi.Language.GetAll();

  const {
    data: apiLanguageData,
    error,
    isLoading,
    mutate: revalidate,
  } = useSWR<ApiSchemaLanguageData[]>(
    shouldLanguageGetAllInvoke ? getAllLanguagesUrl : null,
    () =>
      AxiosAuth.get<
        ApiResponse<
          WithPagination<ApiSchemaLanguageData[]> | ApiSchemaLanguageData[]
        >
      >(getAllLanguagesUrl)
        .then((r) => {
          const payload = r.data?.data as
            | WithPagination<ApiSchemaLanguageData[]>
            | ApiSchemaLanguageData[]
            | undefined;

          if (Array.isArray(payload)) return payload;
          if (payload && Array.isArray((payload as any).data))
            return (payload as any).data as ApiSchemaLanguageData[];

          return [];
        })
        .catch((e) => {
          console.error(e);
          return e;
        })
  );

  const languageList = React.useMemo(() => {
    return Array.isArray(apiLanguageData) ? apiLanguageData : [];
  }, [apiLanguageData]);

  const languageOptions = React.useMemo(
    () =>
      languageList.map((l) => ({
        label: l.code ? `${l.name} (${l.code})` : l.name,
        value: (l as any).id ?? (l as any).languageId ?? l.code ?? l.name,
      })),
    [languageList]
  );

  return {
    languageList,
    languageOptions,
    isLoading,
    error,
    revalidate,
  };
}
